// Cotización del día (Dólares/Euros/Reales a Pesos) para poder sumar y mostrar
// en una sola unidad montos que se cargan en distintas divisas: compras, ventas
// y stock guardan cada uno su propio tipoDivisa suelto, sin cotización asociada.
//
// Antes cada pantalla de Estadísticas (Compras, Ventas) hacía su propio fetch a
// dolarapi.com por separado, y solo sabía convertir Dólares — Euros y Reales no
// tenían cotización en ningún lado y se sumaban directo, como si fueran pesos.
// Esto centraliza el fetch (una sola descarga por sesión, memoizada como
// dataCache.js) y agrega Euros/Reales via el endpoint /v1/cotizaciones.
//
// OJO: esto es la cotización de HOY. Ninguna compra/venta guarda el tipo de
// cambio del día en que se cargó, así que convertir operaciones viejas con la
// cotización actual es una aproximación para comparar montos, no el valor
// real que tuvo esa operación el día que se hizo.

import { useEffect, useState } from 'react';

const ENDPOINT_DOLAR_OFICIAL = 'https://dolarapi.com/v1/dolares/oficial';
const ENDPOINT_DOLAR_CCL = 'https://dolarapi.com/v1/dolares/contadoconliqui';
const ENDPOINT_COTIZACIONES = 'https://dolarapi.com/v1/cotizaciones';

let cachePromise = null;

// { Dólares, DólaresCCL, Euros, Reales } — todos expresados en ARS (venta).
export function fetchCotizaciones() {
  if (!cachePromise) {
    cachePromise = Promise.all([
      fetch(ENDPOINT_DOLAR_OFICIAL).then((r) => r.json()),
      fetch(ENDPOINT_DOLAR_CCL).then((r) => r.json()),
      fetch(ENDPOINT_COTIZACIONES).then((r) => r.json()),
    ])
      .then(([oficial, ccl, cotizaciones]) => {
        const eur = cotizaciones.find((c) => c.moneda === 'EUR');
        const brl = cotizaciones.find((c) => c.moneda === 'BRL');
        return {
          Dólares: oficial?.venta ?? null,
          DólaresCCL: ccl?.venta ?? null,
          Euros: eur?.venta ?? null,
          Reales: brl?.venta ?? null,
        };
      })
      .catch((err) => {
        // No memoizar errores: el próximo llamado reintenta.
        cachePromise = null;
        throw err;
      });
  }
  return cachePromise;
}

// Hook de conveniencia para componentes: { cotizaciones, cargandoCotizaciones }.
export function useCotizaciones() {
  const [cotizaciones, setCotizaciones] = useState(null);
  const [cargandoCotizaciones, setCargandoCotizaciones] = useState(true);
  useEffect(() => {
    let vivo = true;
    fetchCotizaciones()
      .then((c) => {
        if (vivo) setCotizaciones(c);
      })
      .catch((err) => console.error('Error al traer cotizaciones:', err))
      .finally(() => {
        if (vivo) setCargandoCotizaciones(false);
      });
    return () => {
      vivo = false;
    };
  }, []);
  return { cotizaciones, cargandoCotizaciones };
}

// Convierte un monto de la divisa que sea a pesos, usando la cotización de
// venta del día. Si todavía no cargó la cotización, o la divisa es Pesos,
// devuelve el monto tal cual (nunca revienta mientras el fetch está en vuelo).
export function aPesos(monto, tipoDivisa, cotizaciones) {
  const numero = parseFloat(monto) || 0;
  if (!cotizaciones) return numero;
  const cotizacion = cotizaciones[tipoDivisa];
  return cotizacion ? numero * cotizacion : numero;
}
